// Lecture du format des feuilles publiées (lot M4) : les outils STRUCTURÉS de `model.preds` (sans `line`) —
// Pythagore, second degré, baisse en pourcentage — deviennent des lignes de formule et des pièges, exactement comme
// l'ancien moteur (`packages/reasoner/src/kernel-formula.ts`, `pythagoreTools`) : mêmes lignes, libellés, HORA et
// pièges, pour qu'une feuille rapporte autant dans les deux moteurs. Aucune pédagogie nouvelle ici : c'est le
// format des données publiées.

const fmt = (n) => (typeof n === 'number' ? String(n).replace('.', ',') : String(n));

/** → { preds: [{ line, label, why, hora }], traps: [{ line, title, why }] } */
export function expandModelPreds(preds = []) {
  const out = [],
    traps = [];
  for (const p of preds) {
    if (!p || typeof p !== 'object') continue;
    if (p.line) {
      out.push(p);
      continue;
    }
    if (p.skill === 'pythagore' && Array.isArray(p.sides) && p.sides.length === 2 && p.hyp) {
      const [a, b] = p.sides,
        h = p.hyp;
      out.push({
        line: `${h}² = ${a}² + ${b}²`,
        label: 'Pythagore',
        hora: 5,
        why: `Le triangle est rectangle en ${p.right}, donc [${h}] est l’hypoténuse : ${h}² = ${a}² + ${b}².`,
      });
      const hypo = `L’angle droit est en ${p.right} : l’hypoténuse est le côté opposé, [${h}]. C’est ${h}² qui est seul d’un côté.`;
      const plus =
        'Pour obtenir le carré de l’hypoténuse, on ADDITIONNE les carrés des deux autres côtés.';
      traps.push(
        {
          line: `${h} = ${a} + ${b}`,
          title: 'Additionner les longueurs',
          why: `Pythagore relie les CARRÉS des longueurs : ${h}² = ${a}² + ${b}².`,
        },
        {
          line: `${h}² = ${a} + ${b}`,
          title: 'Il manque les carrés',
          why: `Ce sont les carrés des côtés qui s’additionnent : ${a}² + ${b}².`,
        },
        { line: `${a}² = ${h}² + ${b}²`, title: 'Mauvaise hypoténuse', why: hypo },
        { line: `${b}² = ${h}² + ${a}²`, title: 'Mauvaise hypoténuse', why: hypo },
        { line: `${h}² = ${b}² − ${a}²`, title: 'Soustraire au lieu d’additionner', why: plus },
        { line: `${h}² = ${a}² − ${b}²`, title: 'Soustraire au lieu d’additionner', why: plus },
      );
    } else if (p.skill === 'baisse' && p.remise && p.nouveau) {
      const base = fmt(p.base),
        t = fmt(p.taux);
      out.push(
        {
          line: `${p.remise} = ${t} % × ${base}`,
          label: 'Pourcentage',
          hora: 4,
          why: `Baisser de ${t} %, c’est retirer ${t} % du prix : la remise vaut ${t} % de ${base}.`,
        },
        {
          line: `${p.nouveau} = ${base} − ${p.remise}`,
          label: 'Retirer la remise',
          hora: 3,
          why: 'Le nouveau prix, c’est le prix de départ moins la remise.',
        },
        {
          line: `${p.nouveau} = ${base} × (1 − ${t} %)`,
          label: 'Coefficient multiplicateur',
          hora: 5,
          why: `Baisser de ${t} %, c’est garder ${fmt(100 - Number(p.taux))} % du prix : on multiplie par 1 − ${t} %.`,
        },
      );
      traps.push(
        {
          line: `${p.nouveau} = ${base} − ${t}`,
          title: `−${t} % n’est pas −${t}`,
          why: `${t} % de ${base} ne font pas ${t} : la remise se calcule sur le prix (${t} % × ${base}).`,
        },
        {
          line: `${p.nouveau} = ${t} % × ${base}`,
          title: 'Ça, c’est la remise',
          why: `${t} % × ${base} est le montant de la réduction, pas le nouveau prix. Il faut encore le retirer du prix de départ.`,
        },
      );
    } else if (p.skill === 'secondDegre' && p.delta && p.a && p.b && p.c) {
      out.push({
        line: `${p.delta} = ${p.b}² − 4 × ${p.a} × ${p.c}`,
        label: 'Discriminant',
        hora: 5,
        why: 'Pour ax² + bx + c = 0, on calcule le discriminant : Δ = b² − 4ac.',
      });
      const why = (r0, r1) =>
        `Δ > 0, donc deux solutions : ${r0} = (−b − √Δ) ÷ (2a) et ${r1} = (−b + √Δ) ÷ (2a).`;
      if (Array.isArray(p.roots) && p.roots.length === 2) {
        [
          [p.roots[0], '−'],
          [p.roots[1], '+'],
        ].forEach(([r, s]) =>
          out.push({
            line: `${r} = (−${p.b} ${s} √${p.delta}) ÷ (2 × ${p.a})`,
            label: 'Formule des racines',
            hora: 5,
            why: why(p.roots[0], p.roots[1]),
          }),
        );
      }
      if (p.double)
        out.push({
          line: `${p.double} = −${p.b} ÷ (2 × ${p.a})`,
          label: 'Solution double',
          hora: 5,
          why: `Δ = 0, donc une seule solution : ${p.double} = −b ÷ (2a).`,
        });
    }
  }
  return { preds: out, traps };
}
